import React, { useRef, useState } from 'react';
import { Button, Input, Popover, Tooltip } from 'antd';
import { useTranslation } from '../../i18n';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  FileTextOutlined,
  PaperClipOutlined,
  PictureOutlined,
  SendOutlined,
  SmileOutlined,
} from '@ant-design/icons';

const EMOJIS = [
  '😀', '😂', '😊', '😍', '🤝', '👍', '👏', '🙏', '🎉', '🔥',
  '💯', '❤️', '✨', '🌟', '📦', '🚚', '💰', '🎁', '📣', '✅',
  '👌', '😉', '🤔', '😅', '🥰', '💪', '🙌', '📸', '🛍️', '⏰',
];

type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  disabledReason?: string;
  sending?: boolean;
  maxLength?: number;
  placeholder?: string;
  onPickImage?: (file: File) => void;
  onPickFile?: (file: File) => void;
  onOpenTemplate?: () => void;
  sendLabel?: React.ReactNode;
  /** `modEnter` 用 Ctrl/⌘+Enter 发送。`ctrlNewline` 用 Enter 发送，Ctrl/⌘+Enter 换行。 */
  submitOn?: 'enter' | 'modEnter' | 'ctrlNewline';
  /** Rendered before the send button. */
  extraActions?: React.ReactNode;
  /** `stacked` puts a taller input above an action row, suited to long-form replies like email. */
  layout?: 'inline' | 'stacked';
};

export function ChatComposer({
  value,
  onChange,
  onSend,
  disabledReason,
  sending = false,
  maxLength = 2000,
  placeholder,
  onPickImage,
  onPickFile,
  onOpenTemplate,
  sendLabel,
  submitOn = 'enter',
  extraActions,
  layout = 'inline',
}: ChatComposerProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('chat.placeholder');
  const resolvedSendLabel = sendLabel ?? t('chat.send');
  const textAreaRef = useRef<TextAreaRef>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);

  const disabled = Boolean(disabledReason);
  const canSend = !disabled && !sending && value.trim().length > 0;

  const submit = () => {
    if (!canSend) {
      return;
    }
    onSend(value.trim());
  };

  const insertAtCursor = (text: string) => {
    const el = textAreaRef.current?.resizableTextArea?.textArea;
    if (!el) {
      onChange(value + text);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next.slice(0, maxLength));
    requestAnimationFrame(() => {
      el.focus();
      const caret = Math.min(start + text.length, maxLength);
      el.setSelectionRange(caret, caret);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') {
      return;
    }
    // keyCode 229 covers browsers that do not set isComposing during IME input.
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }
    if (submitOn === 'ctrlNewline' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      insertAtCursor('\n');
      return;
    }
    if (e.shiftKey) {
      return;
    }
    if (submitOn === 'modEnter' && !(e.ctrlKey || e.metaKey)) {
      return;
    }
    e.preventDefault();
    submit();
  };

  const handleFileChange =
    (handler?: (file: File) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && handler) {
        handler(file);
      }
      e.target.value = '';
    };

  const toolClass =
    'inline-flex size-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-base text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#596ae8] disabled:cursor-not-allowed disabled:opacity-40';

  const textArea = (
    <Input.TextArea
      ref={textAreaRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      maxLength={maxLength}
      autoSize={layout === 'stacked' ? { minRows: 3, maxRows: 8 } : { minRows: 1, maxRows: 5 }}
      className="flex-1 rounded-lg"
    />
  );

  const sendButton = (
    <Button
      type="primary"
      className="wb-btn-primary shrink-0 rounded-lg"
      icon={<SendOutlined />}
      iconPosition="end"
      loading={sending}
      disabled={!canSend}
      onClick={submit}
    >
      {resolvedSendLabel}
    </Button>
  );

  const emojiPanel = (
    <div className="grid w-[260px] grid-cols-10 gap-0.5">
      {EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className="cursor-pointer rounded border-0 bg-transparent p-1 text-lg leading-none hover:bg-slate-100"
          onClick={() => {
            insertAtCursor(emoji);
            setEmojiOpen(false);
          }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );

  return (
    <div className="border-t border-[#eef0f4] bg-white px-5 pb-4 pt-2">
      <div className="mb-1 flex items-center gap-1">
        <Popover
          content={emojiPanel}
          trigger="click"
          open={emojiOpen && !disabled}
          onOpenChange={setEmojiOpen}
          placement="topLeft"
        >
          <button type="button" className={toolClass} disabled={disabled} aria-label={t('chat.emoji')}>
            <SmileOutlined />
          </button>
        </Popover>
        {onPickImage ? (
          <Tooltip title={t('chat.image')}>
            <button
              type="button"
              className={toolClass}
              disabled={disabled}
              aria-label={t('chat.image')}
              onClick={() => imageInputRef.current?.click()}
            >
              <PictureOutlined />
            </button>
          </Tooltip>
        ) : null}
        {onPickFile ? (
          <Tooltip title={t('chat.file')}>
            <button
              type="button"
              className={toolClass}
              disabled={disabled}
              aria-label={t('chat.file')}
              onClick={() => fileInputRef.current?.click()}
            >
              <PaperClipOutlined />
            </button>
          </Tooltip>
        ) : null}
        {onOpenTemplate ? (
          <Tooltip title={t('chat.script')}>
            <button
              type="button"
              className={toolClass}
              disabled={disabled}
              aria-label={t('chat.script')}
              onClick={onOpenTemplate}
            >
              <FileTextOutlined />
            </button>
          </Tooltip>
        ) : null}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange(onPickImage)}
        />
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange(onPickFile)}
        />
      </div>

      {disabledReason ? (
        <div className="mb-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
          {disabledReason}
        </div>
      ) : null}

      {layout === 'stacked' ? (
        <>
          {textArea}
          <div className="mt-2 flex items-center gap-3">
            <span className="text-[11px] text-slate-400">
              {submitOn === 'modEnter'
                ? t('chat.hint_mod')
                : submitOn === 'ctrlNewline'
                  ? t('chat.hint_ctrl_newline')
                  : t('chat.hint_enter')}
            </span>
            <div className="ml-auto flex items-center gap-2">
              {extraActions}
              {sendButton}
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-end gap-3">
          {textArea}
          {extraActions}
          {sendButton}
        </div>
      )}
    </div>
  );
}
